import { motion, useReducedMotion, useMotionValue, useSpring } from "framer-motion";

/**
 * GlowButton — pill-shaped, neon-blue glow, magnetic hover on desktop,
 * scale-down on press. Renders as <a> when `href` is provided, else <button>.
 */
export default function GlowButton({
  children,
  href,
  variant = "blue",
  className = "",
  testId,
  onClick,
  icon = null,
  ...rest
}) {
  const reduced = useReducedMotion();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  const handleMove = (e) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.35);
    y.set((e.clientY - r.top - r.height / 2) * 0.35);
  };
  const reset = () => {
    x.set(0);
    y.set(0);
  };

  const base =
    "relative inline-flex items-center justify-center gap-2 rounded-full font-poppins font-semibold uppercase tracking-[0.12em] text-sm md:text-[15px] px-8 py-4 select-none cursor-pointer transition-[background-color,color,box-shadow] duration-300 will-change-transform";

  const variants = {
    blue:
      "bg-electric text-white shadow-[0_0_25px_rgba(33,112,232,0.55),0_0_60px_rgba(33,112,232,0.22)] hover:shadow-[0_0_38px_rgba(33,112,232,0.85),0_0_100px_rgba(33,112,232,0.4)]",
    white:
      "bg-white text-ink shadow-[0_0_22px_rgba(255,255,255,0.22)] hover:shadow-[0_0_40px_rgba(33,112,232,0.55)]",
    ghost:
      "bg-white/5 text-white border border-white/15 backdrop-blur-md hover:bg-white/10 hover:shadow-[0_0_30px_rgba(33,112,232,0.4)]",
    gold:
      "bg-gold text-ink shadow-[0_0_22px_rgba(245,158,11,0.45)] hover:shadow-[0_0_42px_rgba(245,158,11,0.7)]",
  };

  const isExternal = href && href !== "#";
  const Comp = motion[href ? "a" : "button"];

  const linkProps = href
    ? {
        href,
        target: isExternal ? "_blank" : undefined,
        rel: isExternal ? "noopener noreferrer" : undefined,
      }
    : { type: "button" };

  const handleClick = (e) => {
    // Placeholder links ("#") shouldn't jump the page to the top.
    if (href && !isExternal) e.preventDefault();
    if (onClick) onClick(e);
  };

  return (
    <Comp
      data-testid={testId}
      onClick={handleClick}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      style={{ x: sx, y: sy }}
      whileHover={reduced ? undefined : { scale: 1.04 }}
      whileTap={{ scale: 0.94 }}
      className={`${base} ${variants[variant] || variants.blue} ${className}`}
      {...linkProps}
      {...rest}
    >
      {icon}
      <span>{children}</span>
    </Comp>
  );
}
